import React, { useState, useEffect, useCallback } from 'react';
import { Bot, Settings, ToggleLeft, ToggleRight, Save, CheckCircle, AlertCircle, X, Target, Clock, Users, Zap, Plus, Edit3, Trash2 } from 'lucide-react';
import api from '../utils/api';

const AutoTaskAssignmentSettings = ({ user }) => {
  const [autoAssignmentSettings, setAutoAssignmentSettings] = useState({
    enabled: false,
    assignment_mode: 'space_owner', // space_owner, balanced, expertise, workload, strict
    fallback_strategy: 'round_robin', // round_robin, least_busy, random, admin_assign
    confidence_threshold: 0.7,
    max_auto_assignments_per_user_daily: 5,
    respect_user_availability: true,
    consider_user_workload: true,
    priority_boost_for_expertise_match: 1.5,
    assignment_delay_minutes: 0,
    notification_settings: {
      notify_assignee: true,
      notify_admin: false,
      notify_project_manager: true
    }
  });

  const [assignmentRules, setAssignmentRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });
  const [stats, setStats] = useState({
    total_auto_assignments_today: 0,
    success_rate_last_7_days: 0,
    avg_assignment_time_seconds: 0
  });

  // Available data for rules
  const [taskCategories, setTaskCategories] = useState([]);
  const [userDomains, setUserDomains] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [defaultStages, setDefaultStages] = useState([]);

  // Rule management
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleFormData, setRuleFormData] = useState({
    name: '',
    description: '',
    enabled: true,
    priority: 0,
    conditions: {},
    assignment_target_type: 'user',
    assignment_target_id: '',
    fallback_user_id: '',
    trigger_on: 'task_create',
    stage_transition: null,
    condition_operator: 'AND'
  });

  useEffect(() => {
    fetchAutoAssignmentSettings();
    fetchAssignmentRules();
    fetchAssignmentStats();
    fetchReferenceData();
  }, []);

  const fetchAutoAssignmentSettings = async () => {
    try {
      const response = await api.get('/auto-assignment-settings');
      
      if (response.data) {
        setAutoAssignmentSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Failed to fetch auto-assignment settings:', error);
      // Settings don't exist yet, use defaults
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignmentRules = async () => {
    try {
      const response = await api.get('/auto-assignment-rules');
      const normalizedRules = (response.data || []).map(rule => {
        let conditions = rule.conditions;
        if (typeof conditions === 'string') {
          try {
            conditions = JSON.parse(conditions);
          } catch (e) {
            conditions = {};
          }
        }
        let stageTransition = rule.stage_transition;
        if (typeof stageTransition === 'string') {
          try {
            stageTransition = JSON.parse(stageTransition);
          } catch (e) {
            stageTransition = null;
          }
        }
        return {
          ...rule,
          conditions: conditions || {},
          stage_transition: stageTransition || null
        };
      });
      setAssignmentRules(normalizedRules);
    } catch (error) {
      console.error('Failed to fetch assignment rules:', error);
    }
  };

  const fetchAssignmentStats = async () => {
    try {
      const response = await api.get('/auto-assignment-stats');
      if (response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch assignment stats:', error);
    }
  };

  const fetchReferenceData = async () => {
    try {
      // Fetch task categories
      const categoriesResponse = await api.get('/task-categories');
      setTaskCategories(categoriesResponse.data);

      // Fetch user domains
      const domainsResponse = await api.get('/user-domains');
      setUserDomains(domainsResponse.data);

      // Fetch team members
      const teamResponse = await api.get('/team');
      setTeamMembers(teamResponse.data.members || []);

      // Fetch default project stages
      const stagesResponse = await api.get('/default-project-stages');
      setDefaultStages(stagesResponse.data || []);
    } catch (error) {
      console.error('Failed to fetch reference data:', error);
    }
  };

  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => setNotification({ show: false, type: '', message: '' }), 5000);
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/auto-assignment-settings', autoAssignmentSettings);
      
      showNotification('success', 'Auto-assignment settings saved successfully');
    } catch (error) {
      console.error('Failed to save settings:', error);
      showNotification('error', 'Failed to save auto-assignment settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleAutoAssignment = async () => {
    const newEnabled = !autoAssignmentSettings.enabled;
    setAutoAssignmentSettings(prev => ({ ...prev, enabled: newEnabled }));
    
    // Auto-save the toggle
    try {
      await api.put('/auto-assignment-settings', {
        ...autoAssignmentSettings,
        enabled: newEnabled
      });
      
      showNotification('success', 
        newEnabled ? 'Auto-assignment enabled' : 'Auto-assignment disabled'
      );
    } catch (error) {
      console.error('Failed to toggle auto-assignment:', error);
      setAutoAssignmentSettings(prev => ({ ...prev, enabled: !newEnabled })); // Revert on error
      showNotification('error', 'Failed to update auto-assignment status');
    }
  };

  const testAssignmentRules = async () => {
    try {
      const response = await api.post('/auto-assignment-test', {});
      
      showNotification('success', `Test completed. Found ${response.data.potential_assignments} potential assignments.`);
    } catch (error) {
      console.error('Failed to test assignment rules:', error);
      showNotification('error', 'Failed to test assignment rules');
    }
  };

  // Rule management functions
  const handleCreateRule = () => {
    setRuleFormData({
      name: '',
      description: '',
      enabled: true,
      priority: 0,
      conditions: {},
      assignment_target_type: 'user',
      assignment_target_id: '',
      fallback_user_id: '',
      trigger_on: 'task_create',
      stage_transition: null,
      condition_operator: 'AND'
    });
    setEditingRule(null);
    setShowRuleModal(true);
  };

  const handleEditRule = (rule) => {
    let conditions = rule.conditions;
    if (typeof conditions === 'string') {
      try {
        conditions = JSON.parse(conditions);
      } catch (e) {
        conditions = {};
      }
    }
    let stageTransition = rule.stage_transition;
    if (typeof stageTransition === 'string') {
      try {
        stageTransition = JSON.parse(stageTransition);
      } catch (e) {
        stageTransition = null;
      }
    }
    setRuleFormData({
      name: rule.name,
      description: rule.description || '',
      enabled: rule.enabled,
      priority: rule.priority || 0,
      conditions: conditions || {},
      assignment_target_type: rule.assignment_target_type,
      assignment_target_id: rule.assignment_target_id || '',
      fallback_user_id: rule.fallback_user_id || '',
      trigger_on: rule.trigger_on || 'task_create',
      stage_transition: stageTransition || null,
      condition_operator: rule.condition_operator || 'AND'
    });
    setEditingRule(rule);
    setShowRuleModal(true);
  };

  const handleSaveRule = async () => {
    if (!ruleFormData.name) {
      showNotification('error', 'Rule name is required');
      return;
    }

    if (!ruleFormData.assignment_target_type) {
      showNotification('error', 'Assignment target type is required');
      return;
    }

    if (['user', 'domain'].includes(ruleFormData.assignment_target_type) && !ruleFormData.assignment_target_id) {
      showNotification('error', 'Please select a target user or domain');
      return;
    }

    if (ruleFormData.trigger_on === 'task_create' && !ruleFormData.conditions?.category_id) {
      showNotification('error', 'Task category is required for category-change rules');
      return;
    }

    setSaving(true);
    try {
      if (editingRule) {
        await api.put(`/auto-assignment-rules/${editingRule.id}`, ruleFormData);
        showNotification('success', 'Rule updated successfully');
      } else {
        await api.post('/auto-assignment-rules', ruleFormData);
        showNotification('success', 'Rule created successfully');
      }
      
      setShowRuleModal(false);
      fetchAssignmentRules();
    } catch (error) {
      console.error('Failed to save rule:', error);
      showNotification('error', 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) {
      return;
    }

    try {
      await api.delete(`/auto-assignment-rules/${ruleId}`);
      
      showNotification('success', 'Rule deleted successfully');
      fetchAssignmentRules();
    } catch (error) {
      console.error('Failed to delete rule:', error);
      showNotification('error', 'Failed to delete rule');
    }
  };

  const handleToggleRule = async (rule) => {
    try {
      await api.put(`/auto-assignment-rules/${rule.id}`, {
        ...rule,
        enabled: !rule.enabled
      });
      
      showNotification('success', `Rule ${!rule.enabled ? 'enabled' : 'disabled'}`);
      fetchAssignmentRules();
    } catch (error) {
      console.error('Failed to toggle rule:', error);
      showNotification('error', 'Failed to update rule');
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-6 bg-gray-200 rounded w-1/4"></div>
        <div className="space-y-4">
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Notification */}
      {notification.show && (
        <div className={`fixed top-4 right-4 z-50 flex items-center space-x-2 px-4 py-3 rounded-lg shadow-lg ${
          notification.type === 'success' 
            ? 'bg-green-100 border border-green-200 text-green-800'
            : 'bg-red-100 border border-red-200 text-red-800'
        }`}>
          {notification.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span className="text-sm font-medium">{notification.message}</span>
          <button
            onClick={() => setNotification({ show: false, type: '', message: '' })}
            className="text-current opacity-70 hover:opacity-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="space-y-6">
        {/* Header with Toggle */}
        <div className="flex items-center justify-between p-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-100">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
              <Bot className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Auto Task Assignment</h3>
              <p className="text-sm text-gray-600">
                Automatically assign tasks based on expertise, workload, and availability
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <p className="text-sm font-medium text-gray-700">
                Status: <span className={autoAssignmentSettings.enabled ? 'text-green-600' : 'text-gray-500'}>
                  {autoAssignmentSettings.enabled ? 'Active' : 'Inactive'}
                </span>
              </p>
              <p className="text-xs text-gray-500">
                {stats.total_auto_assignments_today} assignments today
              </p>
            </div>
            <button
              onClick={toggleAutoAssignment}
              className={`p-2 rounded-lg transition-colors ${
                autoAssignmentSettings.enabled 
                  ? 'text-green-600 bg-green-100 hover:bg-green-200' 
                  : 'text-gray-400 bg-gray-100 hover:bg-gray-200'
              }`}
            >
              {autoAssignmentSettings.enabled ? (
                <ToggleRight className="w-8 h-8" />
              ) : (
                <ToggleLeft className="w-8 h-8" />
              )}
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Target className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.success_rate_last_7_days}%</p>
                <p className="text-sm text-gray-600">Success Rate (7 days)</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.avg_assignment_time_seconds}s</p>
                <p className="text-sm text-gray-600">Avg Assignment Time</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.total_auto_assignments_today}</p>
                <p className="text-sm text-gray-600">Assignments Today</p>
              </div>
            </div>
          </div>
        </div>

        {/* Assignment Configuration */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-6">Assignment Configuration</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Assignment Mode */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assignment Mode
              </label>
              <select
                value={autoAssignmentSettings.assignment_mode}
                onChange={(e) => setAutoAssignmentSettings(prev => ({
                  ...prev,
                  assignment_mode: e.target.value
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="space_owner">Space Owner (Simple)</option>
                <option value="balanced">Balanced (Expertise + Workload)</option>
                <option value="expertise">Expertise Priority (Flexible)</option>
                <option value="workload">Workload Balance</option>
                <option value="strict">Strict Domain Matching (Advanced/Expert Only)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {autoAssignmentSettings.assignment_mode === 'space_owner' 
                  ? 'Assign all tasks to the team/workspace owner - perfect for single-member teams'
                  : autoAssignmentSettings.assignment_mode === 'expertise'
                  ? 'Prefer domain experts but flexible - can assign to intermediate users if experts unavailable'
                  : autoAssignmentSettings.assignment_mode === 'strict'
                  ? '⚠️ STRICT: Only assigns to Advanced/Expert users in exact domains. Leaves unassigned if no expert available.'
                  : autoAssignmentSettings.assignment_mode === 'workload'
                  ? 'Focus on user availability and current task load over expertise'
                  : autoAssignmentSettings.assignment_mode === 'balanced'
                  ? 'Balance both expertise level and current workload for optimal assignment'
                  : 'How to prioritize assignments when multiple users could handle a task'
                }
              </p>
            </div>

          {/* Strict Mode Warning */}
          {autoAssignmentSettings.assignment_mode === 'strict' && (
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <h4 className="text-sm font-semibold text-amber-800">Strict Domain Matching Guidelines</h4>
                  <div className="mt-2 text-xs text-amber-700 space-y-1">
                    <p>• <strong>Only assigns to Advanced/Expert users</strong> in the exact required domains</p>
                    <p>• <strong>Leaves tasks unassigned</strong> if no qualified expert is available</p>
                    <p>• <strong>Ignores fallback strategies</strong> - no round-robin or random assignment</p>
                    <p>• <strong>Respects workload limits</strong> more strictly (80% threshold vs 100%)</p>
                    <p>• <strong>Perfect for critical tasks</strong> that absolutely need domain expertise</p>
                    <p>• <strong>Requires proper domain setup</strong> in Task Categories and User Domain Specialties</p>
                  </div>
                </div>
              </div>
            </div>
          )}

            {/* Fallback Strategy */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fallback Strategy
              </label>
              <select
                value={autoAssignmentSettings.fallback_strategy}
                onChange={(e) => setAutoAssignmentSettings(prev => ({
                  ...prev,
                  fallback_strategy: e.target.value
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="round_robin">Round Robin</option>
                <option value="least_busy">Assign to Least Busy</option>
                <option value="random">Random Assignment</option>
                <option value="admin_assign">Leave for Admin</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                What to do when no expertise match is found
              </p>
            </div>

            {/* Confidence Threshold */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confidence Threshold: {Math.round(autoAssignmentSettings.confidence_threshold * 100)}%
              </label>
              <input
                type="range"
                min="0.3"
                max="1.0"
                step="0.05"
                value={autoAssignmentSettings.confidence_threshold}
                onChange={(e) => setAutoAssignmentSettings(prev => ({
                  ...prev,
                  confidence_threshold: parseFloat(e.target.value)
                }))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>30% (More assignments)</span>
                <span>100% (Fewer, more certain)</span>
              </div>
            </div>

            {/* Max Daily Assignments */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max Auto Assignments per User/Day
              </label>
              <input
                type="number"
                min="1"
                max="20"
                value={autoAssignmentSettings.max_auto_assignments_per_user_daily}
                onChange={(e) => setAutoAssignmentSettings(prev => ({
                  ...prev,
                  max_auto_assignments_per_user_daily: parseInt(e.target.value) || 5
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Prevent overwhelming users with too many auto-assignments
              </p>
            </div>
          </div>

          {/* Advanced Options */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h5 className="text-md font-semibold text-gray-900 mb-4">Advanced Options</h5>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="respect_availability"
                  checked={autoAssignmentSettings.respect_user_availability}
                  onChange={(e) => setAutoAssignmentSettings(prev => ({
                    ...prev,
                    respect_user_availability: e.target.checked
                  }))}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <label htmlFor="respect_availability" className="text-sm text-gray-700">
                  Respect user availability status
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="consider_workload"
                  checked={autoAssignmentSettings.consider_user_workload}
                  onChange={(e) => setAutoAssignmentSettings(prev => ({
                    ...prev,
                    consider_user_workload: e.target.checked
                  }))}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <label htmlFor="consider_workload" className="text-sm text-gray-700">
                  Consider current user workload
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assignment Delay (minutes)
                </label>
                <input
                  type="number"
                  min="0"
                  max="60"
                  value={autoAssignmentSettings.assignment_delay_minutes}
                  onChange={(e) => setAutoAssignmentSettings(prev => ({
                    ...prev,
                    assignment_delay_minutes: parseInt(e.target.value) || 0
                  }))}
                  className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Wait before auto-assigning to allow manual assignment
                </p>
              </div>
            </div>
          </div>

          {/* Notification Settings */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h5 className="text-md font-semibold text-gray-900 mb-4">Notification Settings</h5>
            
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="notify_assignee"
                  checked={autoAssignmentSettings.notification_settings.notify_assignee}
                  onChange={(e) => setAutoAssignmentSettings(prev => ({
                    ...prev,
                    notification_settings: {
                      ...prev.notification_settings,
                      notify_assignee: e.target.checked
                    }
                  }))}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <label htmlFor="notify_assignee" className="text-sm text-gray-700">
                  Notify assigned user
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="notify_admin"
                  checked={autoAssignmentSettings.notification_settings.notify_admin}
                  onChange={(e) => setAutoAssignmentSettings(prev => ({
                    ...prev,
                    notification_settings: {
                      ...prev.notification_settings,
                      notify_admin: e.target.checked
                    }
                  }))}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <label htmlFor="notify_admin" className="text-sm text-gray-700">
                  Notify administrators
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="notify_pm"
                  checked={autoAssignmentSettings.notification_settings.notify_project_manager}
                  onChange={(e) => setAutoAssignmentSettings(prev => ({
                    ...prev,
                    notification_settings: {
                      ...prev.notification_settings,
                      notify_project_manager: e.target.checked
                    }
                  }))}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <label htmlFor="notify_pm" className="text-sm text-gray-700">
                  Notify project manager
                </label>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={testAssignmentRules}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Zap className="w-4 h-4" />
              <span>Test Rules</span>
            </button>

            <button
              onClick={handleSaveSettings}
              disabled={saving}
              className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 transition-all shadow-md"
            >
              {saving && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
              <Save className="w-4 h-4" />
              <span>{saving ? 'Saving...' : 'Save Settings'}</span>
            </button>
          </div>
        </div>

        {/* Assignment Rules Management */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h4 className="text-lg font-semibold text-gray-900">Assignment Rules</h4>
              <p className="text-sm text-gray-600 mt-1">Define conditions and actions for automatic task assignment</p>
            </div>
            <button
              onClick={handleCreateRule}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Create Rule</span>
            </button>
          </div>

          {assignmentRules.length > 0 ? (
            <div className="space-y-3">
              {assignmentRules.map((rule) => (
                <div 
                  key={rule.id} 
                  className={`p-4 rounded-lg border ${rule.enabled ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200 opacity-60'}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h5 className="font-semibold text-gray-900">{rule.name}</h5>
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                          Priority: {rule.priority}
                        </span>
                        {rule.enabled ? (
                          <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">Active</span>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">Inactive</span>
                        )}
                      </div>
                      {rule.description && (
                        <p className="text-sm text-gray-600 mb-2">{rule.description}</p>
                      )}
                      <div className="flex items-center flex-wrap gap-2 text-xs">
                        <span className="px-2 py-1 rounded bg-purple-100 text-purple-700">
                          {rule.trigger_on === 'status_change' ? '🔄 Status Change' : '✨ Category Change'}
                        </span>
                        {rule.stage_transition && (
                          <span className="px-2 py-1 rounded bg-blue-100 text-blue-700">
                            {rule.stage_transition.from === '*' ? 'Any' : rule.stage_transition.from} → {rule.stage_transition.to}
                          </span>
                        )}
                        <span className="text-gray-500">Target: {rule.assignment_target_type}</span>
                        {rule.match_count > 0 && <span className="text-gray-500">Matches: {rule.match_count}</span>}
                        {rule.success_count > 0 && <span className="text-green-600">Success: {rule.success_count}</span>}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleToggleRule(rule)}
                        className="p-2 text-gray-600 hover:text-purple-600 transition-colors"
                        title={rule.enabled ? 'Disable rule' : 'Enable rule'}
                      >
                        {rule.enabled ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                      </button>
                      <button
                        onClick={() => handleEditRule(rule)}
                        className="p-2 text-gray-600 hover:text-blue-600 transition-colors"
                        title="Edit rule"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteRule(rule.id)}
                        className="p-2 text-gray-600 hover:text-red-600 transition-colors"
                        title="Delete rule"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <Target className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p className="font-medium">No assignment rules yet</p>
              <p className="text-sm mt-1">Create your first rule to automate task assignment</p>
            </div>
          )}
        </div>
      </div>

      {/* Rule Creation/Edit Modal */}
      {showRuleModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingRule ? 'Edit Assignment Rule' : 'Create Assignment Rule'}
              </h2>
              <button
                onClick={() => setShowRuleModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Basic Information */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rule Name *
                </label>
                <input
                  type="text"
                  value={ruleFormData.name}
                  onChange={(e) => setRuleFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., Assign design tasks to design team"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={ruleFormData.description}
                  onChange={(e) => setRuleFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  rows="2"
                  placeholder="What does this rule do?"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority (Higher = First)
                  </label>
                  <input
                    type="number"
                    value={ruleFormData.priority}
                    onChange={(e) => setRuleFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div className="flex items-center space-x-2 pt-8">
                  <input
                    type="checkbox"
                    id="rule_enabled"
                    checked={ruleFormData.enabled}
                    onChange={(e) => setRuleFormData(prev => ({ ...prev, enabled: e.target.checked }))}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <label htmlFor="rule_enabled" className="text-sm font-medium text-gray-700">
                    Rule Enabled
                  </label>
                </div>
              </div>

              {/* Trigger Type */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Trigger (When to activate)</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Trigger this rule when:
                  </label>
                  <select
                    value={ruleFormData.trigger_on}
                    onChange={(e) => {
                      const trigger = e.target.value;
                      setRuleFormData(prev => ({ 
                        ...prev, 
                        trigger_on: trigger,
                        // Reset stage_transition if not status_change
                        stage_transition: trigger === 'status_change' ? { from: '*', to: '' } : null
                      }));
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="task_create">Category is set/changed</option>
                    <option value="status_change">Task status changes</option>
                  </select>
                </div>

                {/* Stage Transition Configuration */}
                {ruleFormData.trigger_on === 'status_change' && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Stage Transition</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-2">
                          From Status
                        </label>
                        <select
                          value={ruleFormData.stage_transition?.from || '*'}
                          onChange={(e) => setRuleFormData(prev => ({
                            ...prev,
                            stage_transition: {
                              ...prev.stage_transition,
                              from: e.target.value
                            }
                          }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                          <option value="*">Any Status</option>
                          {defaultStages.map(stage => (
                            <option key={stage.id} value={stage.name}>{stage.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-2">
                          To Status *
                        </label>
                        <select
                          value={ruleFormData.stage_transition?.to || ''}
                          onChange={(e) => setRuleFormData(prev => ({
                            ...prev,
                            stage_transition: {
                              ...prev.stage_transition,
                              to: e.target.value
                            }
                          }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                          <option value="">Select target status...</option>
                          {defaultStages.map(stage => (
                            <option key={stage.id} value={stage.name}>{stage.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                      💡 Example: From &ldquo;Any Status&rdquo; to &ldquo;In Review&rdquo; triggers when any task moves to In Review
                    </p>
                  </div>
                )}
              </div>

              {/* Conditions */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Additional Conditions
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    {ruleFormData.trigger_on === 'task_create' ? '(Category required)' : '(Optional)'}
                  </span>
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {ruleFormData.trigger_on === 'task_create'
                        ? 'Task Category (Required):'
                        : 'If Task Category is:'}
                    </label>
                    <select
                      value={ruleFormData.conditions.category_id || ''}
                      onChange={(e) => setRuleFormData(prev => ({
                        ...prev,
                        conditions: { ...prev.conditions, category_id: e.target.value }
                      }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">Any Category</option>
                      {taskCategories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      And Task Priority is:
                    </label>
                    <select
                      value={ruleFormData.conditions.priority || ''}
                      onChange={(e) => setRuleFormData(prev => ({
                        ...prev,
                        conditions: { ...prev.conditions, priority: e.target.value }
                      }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">Any Priority</option>
                      <option value="urgent">Urgent</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Action (Then assign to)</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assignment Type *
                    </label>
                    <select
                      value={ruleFormData.assignment_target_type}
                      onChange={(e) => setRuleFormData(prev => ({ 
                        ...prev, 
                        assignment_target_type: e.target.value,
                        assignment_target_id: '' // Reset target when type changes
                      }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="user">Specific User</option>
                      <option value="domain">Users with Domain Expertise</option>
                      <option value="project_manager">Project Manager</option>
                      <option value="account_manager">Account Manager</option>
                      <option value="round_robin">Round Robin (Rotate)</option>
                      <option value="least_busy">Least Busy User</option>
                    </select>
                  </div>

                  {ruleFormData.assignment_target_type === 'user' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select User *
                      </label>
                      <select
                        value={ruleFormData.assignment_target_id}
                        onChange={(e) => setRuleFormData(prev => ({ ...prev, assignment_target_id: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="">Choose a user...</option>
                        {teamMembers.map(member => (
                          <option key={member.user.id} value={member.user.id}>
                            {member.user.name} ({member.user.email})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {ruleFormData.assignment_target_type === 'domain' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Domain *
                      </label>
                      <select
                        value={ruleFormData.assignment_target_id}
                        onChange={(e) => setRuleFormData(prev => ({ ...prev, assignment_target_id: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="">Choose a domain...</option>
                        {userDomains.map(domain => (
                          <option key={domain.id} value={domain.id}>
                            {domain.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {ruleFormData.assignment_target_type === 'project_manager' && (
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm text-blue-800">
                        ℹ️ This will assign tasks to the Project Manager set on the project or client.
                        The system will first check the project&apos;s PM, then fallback to the client&apos;s PM if needed.
                      </p>
                    </div>
                  )}

                  {ruleFormData.assignment_target_type === 'account_manager' && (
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm text-blue-800">
                        ℹ️ This will assign tasks to the Account Manager set on the client.
                        Great for client-facing tasks that need account oversight.
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fallback User (Optional)
                    </label>
                    <select
                      value={ruleFormData.fallback_user_id}
                      onChange={(e) => setRuleFormData(prev => ({ ...prev, fallback_user_id: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">No fallback</option>
                      {teamMembers.map(member => (
                        <option key={member.user.id} value={member.user.id}>
                          {member.user.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      User to assign if primary assignment fails
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowRuleModal(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveRule}
                  disabled={saving}
                  className="flex items-center space-x-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                >
                  {saving && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                  <Save className="w-4 h-4" />
                  <span>{saving ? 'Saving...' : (editingRule ? 'Update Rule' : 'Create Rule')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AutoTaskAssignmentSettings;
